import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import emailChanges from '../../../../data/emailChanges';
import { timestampToDate } from '../../../../utils/firebase-timestamp-transformation';
import './TaskCommentsTab.scss';

const TaskCommentsTab = () => {
  const [taskComments, setTaskComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [migratedCount, setMigratedCount] = useState(0);

  useEffect(() => {
    fetchTaskComments();
  }, []);

  const fetchTaskComments = async () => {
    try {
      setLoading(true);
      
      // Access the taskComments subcollection
      const taskCommentsDocRef = doc(db, 'otherDBs', 'taskComments');
      const taskCommentsCollectionRef = collection(taskCommentsDocRef, 'taskComments');
      const querySnapshot = await getDocs(taskCommentsCollectionRef);
      
      const commentsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setTaskComments(commentsList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching task comments:', error);
      setLoading(false);
    }
  };

  // Function to find new email for an employee from emailChanges.js
  const findNewEmail = (oldEmail) => {
    const change = emailChanges.find(change => change.oldEmail === oldEmail);
    return change ? change.newEmail : null;
  };

  // Handle migrate all comments
  const handleMigrateAll = async () => {
    if (!window.confirm(`Are you sure you want to migrate all comments? This will update ${taskComments.length} records.`)) {
      return;
    }
    
    try {
      setMigrating(true);
      
      // Find comments that need migration
      const commentsToMigrate = taskComments.filter(comment => {
        // Skip already migrated comments
        if (comment.migrated) return false;
        
        // Skip if commentedBy doesn't exist or has no mapping
        return comment.commentedBy && findNewEmail(comment.commentedBy);
      });
      
      setProgress({ current: 0, total: commentsToMigrate.length });
      
      // Initialize batch
      let batch = writeBatch(db);
      let batchCount = 0;
      let totalMigrated = 0;
      
      // Process comments in batches (Firestore has a limit of 500 operations per batch)
      for (let i = 0; i < commentsToMigrate.length; i++) {
        const comment = commentsToMigrate[i];
        const oldEmail = comment.commentedBy;
        const newEmail = findNewEmail(oldEmail);
        
        if (newEmail) {
          const commentRef = doc(db, 'otherDBs', 'taskComments', 'taskComments', comment.id);
          
          batch.update(commentRef, {
            commentedBy: newEmail,
            oldEmail: oldEmail,
            migrated: true,
            migratedAt: new Date()
          });
          
          batchCount++;
          totalMigrated++;
          
          // If we've reached the batch limit, commit and start a new batch
          if (batchCount >= 450) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }
        }
        
        // Update progress
        setProgress({ current: i + 1, total: commentsToMigrate.length });
      }
      
      // Commit any remaining updates
      if (batchCount > 0) {
        await batch.commit();
      }
      
      setMigratedCount(totalMigrated);
      alert(`Successfully migrated ${totalMigrated} task comments.`);
      
      // Refresh the list
      fetchTaskComments();
      
    } catch (error) {
      console.error('Error migrating task comments:', error);
      alert(`Error migrating task comments: ${error.message}`);
    } finally {
      setMigrating(false);
    }
  };

  const filteredComments = searchTerm 
    ? taskComments.filter(comment => 
        comment.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comment.commentedBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comment.comment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (comment.taskID || comment.taskId)?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : taskComments;

  // Format date for display using the utility function
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const convertedDate = timestampToDate(date);
    return convertedDate ? convertedDate.toLocaleDateString() : 'N/A';
  };

  // Count total number of migratable comments
  const getMigratableCounts = () => {
    const total = taskComments.length;
    const alreadyMigrated = taskComments.filter(comment => comment.migrated).length;
    const canBeMigrated = taskComments.filter(comment => 
      !comment.migrated && 
      comment.commentedBy && 
      findNewEmail(comment.commentedBy)
    ).length;
    const noMapping = total - alreadyMigrated - canBeMigrated;
    
    return { total, alreadyMigrated, canBeMigrated, noMapping };
  };

  const counts = getMigratableCounts();

  return (
    <div className="task-comments-tab">
      <div className="comments-header">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by email, comment, or task ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="refresh-container">
          <button 
            className="refresh-button"
            onClick={fetchTaskComments}
            disabled={loading || migrating}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      <div className="migration-control">
        <div className="stats">
          <div className="stat-item">
            <span className="stat-label">Total Comments:</span>
            <span className="stat-value">{counts.total}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Already Migrated:</span>
            <span className="stat-value">{counts.alreadyMigrated}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Ready to Migrate:</span>
            <span className="stat-value">{counts.canBeMigrated}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">No Email Mapping:</span>
            <span className="stat-value">{counts.noMapping}</span>
          </div>
        </div>
        
        <button 
          className="migrate-all-button"
          onClick={handleMigrateAll}
          disabled={loading || migrating || counts.canBeMigrated === 0}
        >
          {migrating ? 'Migrating...' : 'Migrate All Comments'}
        </button>
      </div>
      
      {migrating && (
        <div className="progress-bar-container">
          <div className="progress-text">
            Migrating {progress.current} of {progress.total} comments...
          </div>
          <div className="progress-bar-wrapper">
            <div 
              className="progress-bar" 
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}
      
      {migratedCount > 0 && !migrating && (
        <div className="success-message">
          Successfully migrated {migratedCount} comments in the last operation.
        </div>
      )}
      
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading task comments...</p>
        </div>
      ) : filteredComments.length > 0 ? (
        <div className="comments-table-container">
          <table className="comments-table">
            <thead>
              <tr>
                <th>Task ID</th>
                <th>Commented By</th>
                <th>Comment</th>
                <th>Created At</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredComments.map(comment => (
                <tr key={comment.id} className={comment.migrated ? 'migrated-row' : ''}>
                  <td>{comment.taskID || comment.taskId || 'N/A'}</td>
                  <td>
                    {comment.commentedBy || 'N/A'}
                    {comment.migrated && (
                      <div>
                        <span className="migrated-badge">Migrated</span>
                        <div className="old-email">Old: {comment.oldEmail}</div>
                      </div>
                    )}
                  </td>
                  <td className="comment-cell">{comment.comment || 'N/A'}</td>
                  <td>{formatDate(comment.createdAt || comment.timestamp)}</td>
                  <td>
                    {comment.migrated ? (
                      <span className="already-migrated">Migrated</span>
                    ) : findNewEmail(comment.commentedBy) ? (
                      <span className="can-migrate">Ready to Migrate</span>
                    ) : (
                      <span className="no-mapping">No Mapping</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">
          <p>No task comments found.</p>
        </div>
      )}
    </div>
  );
};

export default TaskCommentsTab; 